<template>
  <div class="step-franchise">
    <div class="step-header">
      <h2>{{ t('Selecione a Franquia') }}</h2>
      <p class="step-description">
        {{ t('Escolha a franquia para a qual deseja criar a regra de pagamento.') }}
      </p>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Franquias disponíveis:') }}</strong>
        <p>{{ t('Apenas as franquias selecionadas no filtro global estão disponíveis.') }}</p>
        <ul>
          <li>{{ t('A regra de pagamento se aplica a UMA franquia específica') }}</li>
          <li>{{ t('Se o funcionário trabalha em múltiplas franquias, crie regras separadas para cada uma') }}</li>
          <li>{{ t('Após criar, não será possível alterar a franquia') }}</li>
        </ul>
      </div>
    </div>

    <form @submit.prevent="handleNext" class="wizard-form">
      <!-- Franchise Selection with ButtonsList -->
      <div v-if="franchises.length > 0">
        <buttons-list
          data-testid="payroll-payment-rules-step1-franchise-list"
          :itens-select="franchises"
          v-model="wizardData.franchise_id"
          :label="t('Franquias Disponíveis')"
          :placeholder="t('Selecione a franquia')"
          elem-id="franchise-selection"
          :ng-required="true"
        />
      </div>

      <div v-else class="field-info">
        <i class="pi pi-exclamation-triangle"></i>
        {{ t('Nenhuma franquia selecionada no filtro global. Por favor, selecione pelo menos uma franquia no filtro antes de criar regras de pagamento.') }}
      </div>

      <!-- Selected Franchise Preview -->
      <div v-if="selectedFranchise" class="franchise-preview">
        <div class="preview-header">
          <i class="pi pi-building"></i>
          <div class="franchise-info">
            <strong>{{ selectedFranchise.Name }}</strong>
            <small>{{ t('Franquia selecionada') }}</small>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, watch } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ButtonsList from '@/inputs/ButtonsList.vue';
import { useFiltro } from '@/composables/useFiltro';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface WizardData {
  franchise_id?: Id;
  user_id?: Id;
  rule_type?: string;
  commission_structure?: string;
  brackets?: unknown[];
  amount?: number;
  period_type?: string;
  description?: string;
  meta?: string | null;
}

interface WizardMachine {
  wizardData: Ref<WizardData>;
  existingRules?: Ref<unknown[]>;
  existingRule?: Ref<unknown>;
  isReadonlyMode?: Ref<boolean>;
  isEditMode?: Ref<boolean>;
  next: () => void;
  back?: () => void;
  cancel?: () => void;
  reset?: () => void;
}

interface FranchiseOption {
  ID: Id;
  Name: string;
}

// Inject machine from parent
const machine = inject<WizardMachine | null>('wizardMachine', null);

const { t } = useI18n();
const { selectedFranchises } = useFiltro();

// Use wizard data from machine
const wizardData = computed<WizardData>(() => machine?.wizardData.value || {});

const canProceed = computed<boolean>(() => {
  return wizardData.value.franchise_id !== null && wizardData.value.franchise_id !== undefined;
});

// Use selected franchises from filter
const franchises = computed<FranchiseOption[]>(() => {
  return (selectedFranchises.value as Array<{ id: Id; name: string }>).map(f => ({
    ID: f.id,
    Name: f.name
  }));
});

const selectedFranchise = computed<FranchiseOption | null>(() => {
  if (!wizardData.value.franchise_id) return null;
  return franchises.value.find(f => f.ID === wizardData.value.franchise_id) || null;
});

// Debug: Watch franchise_id changes
watch(() => wizardData.value.franchise_id, (newId, oldId) => {
  log('Step1Franchise', 'watch:franchise_id', `Franchise ID changed from ${oldId} to ${newId}`, {
    canProceed: wizardData.value.franchise_id !== null && wizardData.value.franchise_id !== undefined
  });
}, { immediate: true });

const handleNext = (): void => {
  log('Step1Franchise', 'handleNext', 'Attempting to proceed', {
    franchise_id: wizardData.value.franchise_id,
    canProceed: canProceed.value
  });
  machine?.next();
};
</script>

<style scoped>
.step-franchise {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

.step-description {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content p {
  margin: 0 0 0.5rem 0;
  color: var(--blue-900);
  font-size: 0.9rem;
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

.wizard-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.field-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-50);
  border: 1px dashed var(--surface-border);
  border-radius: 6px;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.field-info i {
  color: var(--orange-500);
  font-size: 1.25rem;
}

.franchise-preview {
  padding: 1.25rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.preview-header i {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.franchise-info strong {
  display: block;
  color: var(--text-color);
  font-size: 1rem;
}

.franchise-info small {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}
</style>
